<script setup>
  import { ref, defineProps, computed } from "vue";
  import { Swiper, SwiperSlide } from "swiper/vue";
  import { FreeMode, Navigation, Thumbs } from "swiper/modules";

  import "swiper/css";
  import "swiper/css/free-mode";
  import "swiper/css/navigation";
  import "swiper/css/thumbs";

  const props = defineProps({
    images: { type: Array, required: true },
    isProductBase: { type: Boolean, default: false },
    showDescription: { type: Boolean, default: false }
  });

  const mainSwiper = ref(null);
  const thumbsSwiper = ref(null);
  const isModalOpen = ref(false);
  const currentSlideIndex = ref(0);
  const activeIndex = ref(0);

  const swiperKey = computed(() => {
    let key = `len-${props.images.length}`;
    if (props.images.length > 0) key += `-url-${props.images[0].fullUrl}`;
    return key;
  });

  const currentDescription = computed(() => {
    const index = props.isProductBase ? activeIndex.value - 1 : activeIndex.value;
    return props.images[index]?.description || "";
  });

  const setMainSwiper = (swiper) => { mainSwiper.value = swiper; };
  const setThumbsSwiper = (swiper) => { thumbsSwiper.value = swiper; };
  const onSlideChange = (swiper) => { activeIndex.value = swiper.realIndex; };

  const openModal = (index) => {
    if (index === -1) {
      // Se clicou no slot (Imagem de destaque/SVG)
      currentSlideIndex.value = 0;
    } else {
      // Se clicou nas imagens da galeria (v-for)
      // Se houver base, a galeria começa no índice 1 do modal
      currentSlideIndex.value = props.isProductBase ? index + 1 : index;
    }
    isModalOpen.value = true;
  };

  const closeModal = () => { isModalOpen.value = false; };
</script>

<template>
  <div class="my-swiper-container">
    <swiper
      :key="swiperKey"
      :modules="[FreeMode, Navigation, Thumbs]"
      :thumbs="{ swiper: thumbsSwiper }"
      :space-between="10"
      :loop="true"
      :navigation="true"
      @swiper="setMainSwiper"
      @slideChange="onSlideChange"
      class="mySwiper4"
      id="swiperProduct">

      <swiper-slide v-if="isProductBase">
        <div class="d-flex justify-content-center align-items-center h-100"
             @click="openModal(-1)"
             style="cursor: pointer;">
          <slot name="firstImage"></slot>
        </div>
      </swiper-slide>

      <swiper-slide v-for="(image, index) in images" :key="image.fullUrl">
        <img :src="image.mediaCollection.thumbnail_460x460"
             :alt="image.title"
             @click="openModal(index)"
             style="cursor: pointer;" />
      </swiper-slide>
    </swiper>

    <div v-if="showDescription" class="description-container">
      <transition name="fade-desc" mode="out-in">
        <p :key="activeIndex" class="description-text">{{ currentDescription }}</p>
      </transition>
    </div>

    <swiper
      :key="swiperKey"
      @swiper="setThumbsSwiper"
      :modules="[FreeMode, Navigation, Thumbs]"
      :space-between="10"
      :slides-per-view="4"
      :free-mode="true"
      :loop="true"
      :watch-slides-progress="true"
      class="mySwiper3">

      <swiper-slide v-if="isProductBase">
        <div class="d-flex justify-content-center align-items-center h-100">
          <slot name="firstThumbsImage"></slot>
        </div>
      </swiper-slide>

      <swiper-slide v-for="image in images" :key="image.fullUrl">
        <img class="swiper-image-content" :src="image.mediaCollection.thumbnail_90x90" :alt="image.title" />
      </swiper-slide>
    </swiper>

    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <button @click="closeModal" class="modal-close-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 12 12" fill="none">
            <path d="M11 1L1 11" stroke="#333333" stroke-miterlimit="10"/>
            <path d="M1 1L11 11" stroke="#333333" stroke-miterlimit="10"/>
          </svg>
        </button>

        <swiper
          v-if="isModalOpen"
          :modules="[Navigation]"
          :slides-per-view="1"
          :navigation="true"
          :loop="true"
          :initial-slide="currentSlideIndex"
          class="fullscreen-swiper">

          <swiper-slide v-if="isProductBase">
            <div class="fullscreen-slide-content">
              <slot name="firstImage"></slot>
            </div>
          </swiper-slide>

          <swiper-slide v-for="(item, index) in images" :key="'modal-' + index">
            <div class="fullscreen-slide-content">
              <img v-if="item.type === 'image'" :src="item.fullUrl" :alt="item.title" />
              <video
                v-else-if="item.type === 'video'"
                :src="item.fullUrl"
                controls
                muted
                preload="metadata"
                style="width: 100%; height: 100%"
              ></video>
            </div>
            <p class="fullscreen-description">{{ item.description }}</p>
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </div>
</template>

<style scoped>


  .fade-desc-enter-active, .fade-desc-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-desc-enter-from, .fade-desc-leave-to {
    opacity: 0;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .modal-content {
    position: relative;
    width: 90%;
    height: 80%;
  }
  .modal-close-button {
    position: absolute;
    top: -40px;
    right: 0;
    background: white;
    border: none;
    padding: 10px;
    cursor: pointer;
    z-index: 10000;
  }
  .fullscreen-swiper {
    width: 100%;
    height: 100%;
  }
  .fullscreen-swiper img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .fullscreen-slide-content {
    width: 100%;
    height: calc(100% - 50px);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fullscreen-description {
    text-align: center;
    color: white;
    padding: 10px;
    margin: 0;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
